import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PersonalInfoSection = ({ formData, onChange, validationErrors = {}, isLoading = false }) => {
  const renderFieldError = (fieldName) => {
    const error = validationErrors[fieldName];
    if (error) {
      return (
        <p className="text-sm text-error-600 mt-1">
          {Array.isArray(error) ? error[0] : error}
        </p>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-accent-800 mb-2">
          Personal Information
        </h2>
        <p className="text-accent-600">
          Update your basic information and contact details
        </p>
      </div>

      <div className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              First Name *
            </label>
            <Input
              type="text"
              value={formData?.firstName || ''}
              onChange={(e) => onChange('firstName', e.target.value)}
              placeholder="Enter your first name"
              className={`w-full ${validationErrors.firstName ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading}
            />
            {renderFieldError('firstName')}
          </div>
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Last Name *
            </label>
            <Input
              type="text"
              value={formData?.lastName || ''}
              onChange={(e) => onChange('lastName', e.target.value)}
              placeholder="Enter your last name"
              className={`w-full ${validationErrors.lastName ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading}
            />
            {renderFieldError('lastName')}
          </div>
        </div>

        {/* Email - Read only since it's used for authentication */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Email Address *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Mail" size={18} className="text-accent-400" />
            </div>
            <Input
              type="email"
              value={formData?.email || ''}
              readOnly
              className="w-full pl-10 bg-accent-50 text-accent-500 cursor-not-allowed"
              placeholder="Email address"
            />
          </div>
          <p className="text-sm text-accent-500 mt-1">
            Email address cannot be changed as it's used for account authentication.
          </p>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Phone Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Phone" size={18} className="text-accent-400" />
            </div>
            <Input
              type="tel"
              value={formData?.phoneNumber || ''}
              onChange={(e) => onChange('phoneNumber', e.target.value)}
              placeholder="Enter your phone number"
              className={`w-full pl-10 ${validationErrors.phoneNumber ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading}
            />
          </div>
          {renderFieldError('phoneNumber')}
        </div>

        {/* Date of Birth */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Date of Birth
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Calendar" size={18} className="text-accent-400" />
            </div>
            <Input
              type="date"
              value={formData?.birthDate || ''}
              onChange={(e) => onChange('birthDate', e.target.value)}
              className={`w-full pl-10 ${validationErrors.birthDate ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading}
            />
          </div>
          {renderFieldError('birthDate')}
          <p className="text-sm text-accent-500 mt-1">
            We use this to suggest age-appropriate gifts and celebrate your special day.
          </p>
        </div>

        {/* Gender Selection */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Gender
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="User" size={18} className="text-accent-400" />
            </div>
            <select
              value={formData?.gender || ''}
              onChange={(e) => onChange('gender', e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${validationErrors.gender ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>
          {renderFieldError('gender')}
        </div>

        {/* Account Status Info */}
        <div className="bg-primary-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-primary-800 mb-1">
                Account Information
              </h4>
              <div className="text-sm text-primary-700 space-y-1">
                <p>Account Status: {formData?.isVerified ? 'Verified' : 'Pending Verification'}</p>
                <p>Member Since: {formData?.createdAt ? new Date(formData.createdAt).toLocaleDateString() : 'N/A'}</p>
                {formData?.isPremiumMember && (
                  <p className="text-primary-800 font-medium">✨ Premium Member</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;